import { Stack, Group, Text, Badge, Box } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { TenantName } from '../../../../components/TenantName';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { OUTCOME_COLOR, outcomeOf, type Delivery } from './deliveryState';

const MUTED = 'var(--rr-color-text-muted)';

export function DeliveriesList({ records, onSelect }: { records: Delivery[]; onSelect: (row: Delivery) => void }) {
  const t = useT();
  return (
    <Stack gap="xs">
      {records.map((d) => {
        const outcome = outcomeOf(d);
        const badge = (
          <Badge size="sm" variant="light" color={OUTCOME_COLOR[outcome]}>
            {outcome === 'blocked' ? t('webhooks.deliveries.blocked') : (d.http_status ?? t('status.pending'))}
          </Badge>
        );
        return (
          <button
            key={d.id}
            type="button"
            className="rr-list-row"
            onClick={() => onSelect(d)}
            aria-label={t('webhooks.deliveries.openDetail')}
          >
            <Group gap="md" align="center" wrap="wrap" style={{ width: '100%' }}>
              <Stack gap={0} style={{ flex: 1, minWidth: 220 }}>
                <Group gap="xs" align="baseline" wrap="nowrap">
                  <Text role="subtitle" fw={600} truncate><code>{d.event_type}</code></Text>
                  <Text role="caption" c={MUTED}>{t('webhooks.deliveries.list.attempt', { number: d.attempt_number })}</Text>
                </Group>
                <Group gap="xs" align="baseline" wrap="wrap">
                  <TenantName id={d.organisation_id} subtle />
                  <Text role="caption" c={MUTED}>
                    <time dateTime={d.scheduled_at} title={formatDateTime(d.scheduled_at)}>
                      {t('webhooks.deliveries.list.scheduled', { when: formatRelative(d.scheduled_at) })}
                    </time>
                    {d.latency_ms != null ? ` · ${t('webhooks.deliveries.latencyMs', { value: d.latency_ms })}` : ''}
                  </Text>
                </Group>
                {/* The reason is the whole point of surfacing a refusal, so it
                    is on the row rather than only in a tooltip. */}
                {outcome === 'blocked' && d.blocked_reason && (
                  <Text role="caption" c="var(--rr-color-warning)">
                    {t('webhooks.deliveries.blockedReason', { reason: d.blocked_reason })}
                  </Text>
                )}
              </Stack>
              <Box style={{ flexShrink: 0 }}>
                {outcome === 'blocked'
                  ? <Tooltip label={t('webhooks.deliveries.blockedReason', { reason: d.blocked_reason ?? '' })} position="top" maxWidth={360}>{badge}</Tooltip>
                  : badge}
              </Box>
            </Group>
          </button>
        );
      })}
    </Stack>
  );
}
